import { PractitionerModel } from '@ctw/shared/api/fhir/models/practitioner';
import { findReference } from '@ctw/shared/api/fhir/resource-helper';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { MedicationRequest } from 'fhir/r4';
import { compact } from 'lodash-es';
import { getIdentifyingRxNormCode, getMedicationDisplayName } from '../medication';
import { FHIRModel } from './fhir-model';

export class MedicationRequestModel extends FHIRModel<MedicationRequest> {
  public kind = 'MedicationRequest' as const;

  public get authoredOn() {
    return this.resource.authoredOn ? formatFHIRDate(this.resource.authoredOn) : undefined;
  }

  public get includedRequester() {
    const reference = this.resource.requester;

    const practitioner = findReference(
      'Practitioner',
      this.resource.contained,
      this.includedResources,
      reference,
    );

    if (practitioner) {
      return new PractitionerModel(practitioner).fullName;
    }
    return this.resource.requester?.display;
  }

  public get medicationDisplayName() {
    return getMedicationDisplayName(this.resource, this.includedResources);
  }

  public get pharmacy() {
    const reference = this.resource.dispenseRequest?.performer || {};
    const organization = findReference(
      'Organization',
      this.resource.contained,
      this.includedResources,
      reference,
    );
    if (organization) {
      const telecom = organization.telecom?.[0].value;
      const { city, state, postalCode, text, line = [] } = organization.address?.[0] || {};
      const cityStatePostal = compact([city, `${state} ${postalCode}`]).join(', ');

      return {
        telecom,
        name: organization.name,
        address: text ?? compact([line, cityStatePostal]).join('\n'),
      };
    }
    return { name: reference.display };
  }

  public get rxNorm(): string | undefined {
    return getIdentifyingRxNormCode(this.resource, this.includedResources);
  }

  public get title() {
    return this.medicationDisplayName;
  }
}
